/**
 * Entity compliance and sales tax: the firm-wide filings list (Tax › Entity & Sales Tax), a
 * business's legal form, registrations and filings (the client's Entity & Sales Tax tab), and the
 * due-date rules (Firm Settings › Services and workflows › Compliance rules).
 */
import { COMPLIANCE_STATES, ENTITY_TYPES, formatCents, FREQUENCY_WORDS, parseDollars, RULE_SETTINGS, ruleSettingValueText, type SalesTaxFrequency } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "wouter";
import { ask, askText } from "@/components/dialogs";
import { Empty, ListSearch, Loading, PageHeader, Pager, Section, Segmented, StatRow, useServerPaged } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import { cn } from "@/lib/utils";
import { RuleFigureText, ruleStatusLabel, type RuleStatusView } from "@/components/rule-settings";

const select = "h-9 rounded-md border border-input bg-background px-2 text-sm";
const pill = "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";
const STATES = Object.entries(COMPLIANCE_STATES);

type Filing = {
  id: string;
  clientId: string;
  clientName?: string;
  kind: "entity" | "sales_tax";
  jurisdiction: string;
  name: string;
  fee: string;
  periodLabel: string;
  dueOn: string;
  status: "due" | "filed" | "not_required";
  filedOn: string | null;
  amountCents: number | null;
  confirmation: string;
  notes: string;
  /** Fees (and DC's sales tax rate) as of the due date, each with its status. */
  figures?: Array<RuleStatusView & { label: string }>;
  ruleConfirmed: boolean;
  overdue: boolean;
};

/** "Fee $300 · Not confirmed · Late fee $100 · Not confirmed" under a filing or registration. */
function Figures({ figures }: { figures?: Array<RuleStatusView & { label: string }> }) {
  if (!figures?.length) return null;
  return (
    <span className="block text-xs text-muted-foreground">
      {figures.map((x, i) => (
        <span key={x.key}>
          {i > 0 && "; "}
          <RuleFigureText r={x} label={x.label} />
        </span>
      ))}
    </span>
  );
}

function StatusPill({ f }: { f: Filing }) {
  if (f.status === "filed") return <span className={cn(pill, "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200")}>Filed {formatDate(f.filedOn!)}</span>;
  if (f.status === "not_required") return <span className={cn(pill, "bg-muted text-muted-foreground")}>Not required</span>;
  if (f.overdue) return <span className={cn(pill, "bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200")}>Overdue</span>;
  return <span className={cn(pill, "bg-secondary text-foreground")}>To file</span>;
}

/** Records a filing as filed (date, amount paid, confirmation) or as not required. */
function FileDialog({ f, today, onClose }: { f: Filing; today: string; onClose: () => void }) {
  const [filedOn, setFiledOn] = useState(f.filedOn ?? today);
  const [amount, setAmount] = useState(f.amountCents !== null ? (f.amountCents / 100).toFixed(2) : "");
  const [confirmation, setConfirmation] = useState(f.confirmation);
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: (status: "filed" | "not_required") => {
      let amountCents: number | null = null;
      if (status === "filed" && amount.trim()) amountCents = parseDollars(amount);
      return api.send("PATCH", `/api/compliance/filings/${f.id}`, status === "filed" ? { status, filedOn, amountCents, confirmation } : { status });
    },
    onSuccess: onClose,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record the filing</DialogTitle>
          <DialogDescription>
            {f.jurisdiction} {f.kind === "entity" ? f.name : "sales and use tax return"} · {f.periodLabel} · due {formatDate(f.dueOn)}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Date filed</span>
            <Input type="date" max={today} value={filedOn} onChange={(e) => setFiledOn(e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">{f.kind === "sales_tax" ? "Tax paid" : "Fee or tax paid"} (optional)</span>
            <Input inputMode="decimal" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Confirmation number (optional)</span>
            <Input maxLength={100} value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
          </label>
        </div>
        {f.figures && f.figures.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {f.figures.map((x) => `${x.label}: ${x.value === null ? "" : `${ruleSettingValueText(x.kind, x.value)} · `}${ruleStatusLabel(x)}`).join("; ")}
          </p>
        )}
        {f.fee && <p className="text-xs text-muted-foreground">{f.fee}</p>}
        {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => save.mutate("not_required")} disabled={save.isPending}>
            Not required
          </Button>
          <Button onClick={() => save.mutate("filed")} disabled={save.isPending || !filedOn}>
            Mark filed
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilingRow({ f, canEdit, showClient, onOpen }: { f: Filing; canEdit: boolean; showClient: boolean; onOpen: (f: Filing) => void }) {
  const undo = useMutation({ mutationFn: () => api.send("PATCH", `/api/compliance/filings/${f.id}`, { status: "due" }) });
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
      <span className="w-full shrink-0 tabular text-muted-foreground sm:w-24">{formatDate(f.dueOn)}</span>
      <span className="min-w-[12rem] flex-1">
        {showClient && (
          <Link href={`/clients/${f.clientId}?tab=compliance`} className="font-medium hover:underline">
            {f.clientName}
          </Link>
        )}
        <span className={cn("block", !showClient && "font-medium")}>
          {f.jurisdiction} {f.kind === "entity" ? f.name : "sales and use tax return"} · {f.periodLabel}
        </span>
        {!f.ruleConfirmed && <span className="block text-xs text-amber-700 dark:text-amber-300">Due-date rule not yet confirmed by the firm</span>}
        {f.status === "due" && <Figures figures={f.figures} />}
        {f.status === "filed" && (f.amountCents !== null || f.confirmation) && (
          <span className="block text-xs text-muted-foreground">
            {f.amountCents !== null && `Paid ${formatCents(f.amountCents)}`}
            {f.amountCents !== null && f.confirmation && " · "}
            {f.confirmation && `Confirmation ${f.confirmation}`}
          </span>
        )}
      </span>
      <StatusPill f={f} />
      {canEdit &&
        (f.status === "due" ? (
          <Button size="sm" variant="outline" onClick={() => onOpen(f)}>
            Record
          </Button>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => undo.mutate()} disabled={undo.isPending} title="Mark it as still to file">
            Undo
          </Button>
        ))}
    </li>
  );
}

type FilingsPage = { today: string; total: number; counts: { overdue: number; next30: number; filed: number }; unconfirmedRules: number; items: Filing[] };

/** Tax › Entity & Sales Tax: every client's annual reports and sales tax returns. */
export function ComplianceFilingsPage({ me }: { me: Me }) {
  const [view, setView] = useState<"open" | "filed" | "all">("open");
  const [kind, setKind] = useState<"" | "entity" | "sales_tax">("");
  const [q, setQ] = useState("");
  const { offset, size, paged } = useServerPaged(`${view}${kind}${q}`);
  const qs = new URLSearchParams({ view, offset: String(offset), limit: String(size), ...(kind ? { kind } : {}), ...(q.trim() ? { q: q.trim() } : {}) });
  const { data, isLoading, error } = useQuery<FilingsPage>({ queryKey: [`/api/compliance/filings?${qs}`], placeholderData: (prev) => prev });
  const [open, setOpen] = useState<Filing | null>(null);
  const canEdit = hasPermission(me, "clients.write");
  return (
    <>
      <PageHeader title="Entity & Sales Tax" description="Annual reports, franchise taxes and sales tax returns for the businesses you look after, from each client's registrations." />
      {isLoading || !data ? (
        error ? <Empty>{errorMessage(error)}</Empty> : <Loading />
      ) : (
        <div className="space-y-4">
          <StatRow
            stats={[
              { label: "Overdue", value: data.counts.overdue, tone: data.counts.overdue ? "danger" : "default" },
              { label: "Due in the next 30 days", value: data.counts.next30, tone: data.counts.next30 ? "warning" : "default" },
              { label: "Filed or not required", value: data.counts.filed },
            ]}
          />
          {data.unconfirmedRules > 0 && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              {data.unconfirmedRules} due-date rule{data.unconfirmedRules === 1 ? " has" : "s have"} not been confirmed against the state's own source yet.{" "}
              {hasPermission(me, "firm.manage") ? (
                <Link href="/settings?tab=compliance-rules" className="font-medium underline">
                  Review the rules
                </Link>
              ) : (
                "The firm owner can confirm them in Firm Settings."
              )}
            </p>
          )}
          <Section
            title="Filings"
            description={view === "open" ? "Everything still to file, and what is due in the next 120 days." : view === "filed" ? "Filed or not required, the latest first." : "Every filing due up to 120 days ahead."}
            action={
              <div className="flex flex-wrap items-center gap-2">
                <select aria-label="Kind" className={select} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
                  <option value="">Reports and sales tax</option>
                  <option value="entity">Annual reports and franchise taxes</option>
                  <option value="sales_tax">Sales tax returns</option>
                </select>
                <ListSearch value={q} onChange={setQ} placeholder="Search clients" className="sm:w-56" />
              </div>
            }
          >
            <Segmented
              label="Show"
              className="mb-3"
              value={view}
              onChange={setView}
              options={[
                { value: "open", label: "To file" },
                { value: "filed", label: "Filed" },
                { value: "all", label: "All" },
              ]}
            />
            {data.items.length === 0 ? (
              <Empty>{q.trim() ? "No filings match." : view === "open" ? "Nothing to file. Add a business's registrations on its Entity & Sales Tax tab to track its filings here." : "Nothing here yet."}</Empty>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border">
                {data.items.map((f) => (
                  <FilingRow key={f.id} f={f} canEdit={canEdit} showClient onOpen={setOpen} />
                ))}
              </ul>
            )}
            <Pager paged={paged(data.total)} noun="filings" className="mt-3" />
          </Section>
        </div>
      )}
      {open && data && <FileDialog f={open} today={data.today} onClose={() => setOpen(null)} />}
    </>
  );
}

type Registration = {
  id: string;
  kind: "entity" | "sales_tax";
  jurisdiction: string;
  scope: "domestic" | "foreign" | null;
  frequency: "monthly" | "quarterly" | "annual" | null;
  registeredOn: string;
  endsOn: string | null;
  rules: Array<{ id: string; name: string; fee: string; figures?: Array<RuleStatusView & { label: string }>; confirmed: boolean; sourceUrl: string | null }>;
};
type ClientCompliance = { today: string; entity: { entityType: string | null; formationState: string | null; formedOn: string | null }; registrations: Registration[]; filings: Filing[] };

/** A business's legal form, where it is registered, and the filings that follow. */
export function ClientCompliancePanel({ clientId, me }: { clientId: string; me: Me }) {
  const { data, isLoading, error } = useQuery<ClientCompliance>({ queryKey: [`/api/clients/${clientId}/compliance`] });
  const canEdit = hasPermission(me, "clients.write");
  const [entity, setEntity] = useState<ClientCompliance["entity"] | null>(null);
  const [reg, setReg] = useState<{ kind: "entity" | "sales_tax"; jurisdiction: string; scope: "domestic" | "foreign"; frequency: "monthly" | "quarterly" | "annual"; registeredOn: string }>({ kind: "entity", jurisdiction: "MD", scope: "domestic", frequency: "quarterly", registeredOn: "" });
  const [open, setOpen] = useState<Filing | null>(null);
  const [showAll, setShowAll] = useState(false);
  const saveEntity = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("PATCH", `/api/clients/${clientId}/entity`, entity), onSuccess: () => setEntity(null) });
  const addReg = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", `/api/clients/${clientId}/compliance/registrations`, reg.kind === "entity" ? { kind: "entity", jurisdiction: reg.jurisdiction, scope: reg.scope, registeredOn: reg.registeredOn } : { kind: "sales_tax", jurisdiction: reg.jurisdiction, frequency: reg.frequency, registeredOn: reg.registeredOn }),
    onSuccess: () => setReg({ ...reg, registeredOn: "" }),
  });
  const endReg = useMutation({ meta: { inlineError: true }, mutationFn: (x: { id: string; endsOn: string | null }) => api.send("PATCH", `/api/compliance/registrations/${x.id}`, { endsOn: x.endsOn }) });
  const removeReg = useMutation({ meta: { inlineError: true }, mutationFn: (id: string) => api.send("DELETE", `/api/compliance/registrations/${id}`) });
  if (isLoading) return <Loading />;
  if (error || !data) return <Empty>{errorMessage(error)}</Empty>;
  const e = entity ?? data.entity;
  const editing = entity !== null;
  const upcoming = data.filings.filter((f) => f.status === "due" || f.dueOn >= data.today);
  const shown = showAll ? data.filings : upcoming;
  const err = saveEntity.error ?? addReg.error ?? endReg.error ?? removeReg.error;
  return (
    <div className="space-y-6">
      <Section title="Legal form" description="Which annual reports apply depends on the legal form and where the business is registered.">
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Legal form</span>
            <select aria-label="Legal form" disabled={!canEdit} className={cn(select, "w-full")} value={e.entityType ?? ""} onChange={(ev) => setEntity({ ...e, entityType: ev.target.value || null })}>
              <option value="">Not recorded</option>
              {Object.entries(ENTITY_TYPES).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">State of formation</span>
            <Input aria-label="State of formation" disabled={!canEdit} maxLength={2} placeholder="MD" value={e.formationState ?? ""} onChange={(ev) => setEntity({ ...e, formationState: ev.target.value.toUpperCase() || null })} />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Date formed</span>
            <Input type="date" aria-label="Date formed" disabled={!canEdit} max={data.today} value={e.formedOn ?? ""} onChange={(ev) => setEntity({ ...e, formedOn: ev.target.value || null })} />
          </label>
        </div>
        {canEdit && editing && (
          <div className="mt-3 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEntity(null)}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => saveEntity.mutate()} disabled={saveEntity.isPending}>
              Save
            </Button>
          </div>
        )}
      </Section>

      <Section title="Registrations" description="Where the business is registered (formed there, or registered as a foreign entity) and where it files sales tax, with the frequency the state assigned.">
        {data.registrations.length === 0 ? (
          <Empty>No registrations yet.</Empty>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {data.registrations.map((g) => (
              <li key={g.id} className="flex flex-wrap items-start gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="font-medium">
                    {COMPLIANCE_STATES[g.jurisdiction] ?? g.jurisdiction} · {g.kind === "entity" ? (g.scope === "domestic" ? "Formed here" : "Registered as a foreign entity") : `Sales tax, filed ${FREQUENCY_WORDS[g.frequency as SalesTaxFrequency]}`}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    Since {formatDate(g.registeredOn)}
                    {g.endsOn && ` · ended ${formatDate(g.endsOn)}`}
                  </span>
                  {g.rules.length === 0 ? (
                    <span className="block text-xs text-amber-700 dark:text-amber-300">{g.kind === "entity" && !data.entity.entityType ? "Record the legal form to see which filings apply." : "No due-date rule applies; nothing will be tracked for it."}</span>
                  ) : (
                    g.rules.map((r) => (
                      <span key={r.id} className="block text-xs text-muted-foreground">
                        {r.name}
                        {r.fee && ` · ${r.fee}`}
                        {!r.confirmed && <span className="text-amber-700 dark:text-amber-300"> · rule not yet confirmed</span>}
                        <Figures figures={r.figures} />
                      </span>
                    ))
                  )}
                </span>
                {canEdit && (
                  <span className="flex gap-1">
                    {g.endsOn ? (
                      <Button size="sm" variant="ghost" onClick={() => endReg.mutate({ id: g.id, endsOn: null })}>
                        Reopen
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          if (await ask({ title: "End this registration?", description: "Use this when the business dissolved, withdrew from the state, closed its sales tax account or changed filing frequency (then add the new one). Filings not yet made are no longer tracked from today.", confirmLabel: "End registration" })) endReg.mutate({ id: g.id, endsOn: data.today });
                        }}
                      >
                        End
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        if (await ask({ title: "Delete this registration?", description: "Only a registration with no filings recorded can be deleted.", confirmLabel: "Delete", destructive: true })) removeReg.mutate(g.id);
                      }}
                    >
                      Delete
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <form
            className="mt-3 grid gap-2 sm:grid-cols-[repeat(4,minmax(0,1fr))_auto]"
            onSubmit={(ev) => {
              ev.preventDefault();
              addReg.mutate();
            }}
          >
            <select aria-label="Registration" className={select} value={reg.kind} onChange={(ev) => setReg({ ...reg, kind: ev.target.value as typeof reg.kind })}>
              <option value="entity">Registration</option>
              <option value="sales_tax">Sales tax account</option>
            </select>
            <select aria-label="State" className={select} value={reg.jurisdiction} onChange={(ev) => setReg({ ...reg, jurisdiction: ev.target.value })}>
              {STATES.map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
            {reg.kind === "entity" ? (
              <select aria-label="Formed there or foreign" className={select} value={reg.scope} onChange={(ev) => setReg({ ...reg, scope: ev.target.value as typeof reg.scope })}>
                <option value="domestic">Formed there</option>
                <option value="foreign">Registered as foreign</option>
              </select>
            ) : (
              <select aria-label="Filing frequency" className={select} value={reg.frequency} onChange={(ev) => setReg({ ...reg, frequency: ev.target.value as typeof reg.frequency })}>
                <option value="monthly">Files monthly</option>
                <option value="quarterly">Files quarterly</option>
                <option value="annual">Files annually</option>
              </select>
            )}
            <Input type="date" aria-label="Registered on" required max={data.today} value={reg.registeredOn} onChange={(ev) => setReg({ ...reg, registeredOn: ev.target.value })} />
            <Button type="submit" size="sm" variant="outline" className="h-9" disabled={!reg.registeredOn || addReg.isPending}>
              Add
            </Button>
          </form>
        )}
        {err && <p className="mt-2 text-sm text-destructive">{errorMessage(err)}</p>}
      </Section>

      <Section
        title="Filings"
        description={showAll ? "Every filing from the last year and the year ahead." : "Still to file, and everything due from today on."}
        action={
          <Button size="sm" variant="ghost" onClick={() => setShowAll(!showAll)}>
            {showAll ? "Hide earlier filings" : "Show earlier filings"}
          </Button>
        }
      >
        {shown.length === 0 ? (
          <Empty>No filings. They appear once the business has a registration and a legal form a rule applies to.</Empty>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {shown.map((f) => (
              <FilingRow key={f.id} f={f} canEdit={canEdit} showClient={false} onOpen={setOpen} />
            ))}
          </ul>
        )}
      </Section>
      {open && <FileDialog f={open} today={data.today} onClose={() => setOpen(null)} />}
    </div>
  );
}

type Rule = {
  id: string;
  presetKey: string | null;
  kind: "entity" | "sales_tax";
  jurisdiction: string;
  name: string;
  entityTypes: string[];
  appliesTo: "domestic" | "foreign" | "any";
  frequency: string | null;
  schedule: { kind: string; month?: number; day?: number; periodEndMonth?: number };
  weekend: "none" | "next_business_day" | "previous_business_day";
  fee: string;
  sourceUrl: string | null;
  confirmedAt: string | null;
  active: boolean;
};

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
function scheduleText(r: Rule): string {
  const s = r.schedule;
  const md = s.month && s.day ? `${MONTHS[s.month - 1]} ${s.day}` : "";
  const when = {
    fixed: `Each year by ${md}, from the year after registration`,
    anniversary_month_end: "Each year by the last day of the month the business was formed or registered",
    biennial_fixed: `Every two years by ${md}, from the year after registration`,
    monthly_following: `Each month by the ${s.day}th of the following month`,
    quarterly_following: `Each quarter by the ${s.day}th of the month after the quarter`,
    annual_following: `For the year ending ${MONTHS[(s.periodEndMonth ?? 12) - 1]}, by ${md}`,
  }[s.kind as "fixed"];
  const weekend = r.weekend === "next_business_day" ? "; a weekend or holiday moves it to the next business day" : r.weekend === "previous_business_day" ? "; a weekend or holiday moves it to the business day before" : "";
  return `${when ?? s.kind}${weekend}.`;
}

/** Firm Settings › Compliance rules: confirm each against its source, change it, or switch it off. */
export function ComplianceRulesSettings({ canEdit }: { canEdit: boolean }) {
  const { data, isLoading, error } = useQuery<Rule[]>({ queryKey: ["/api/compliance/rules"] });
  const update = useMutation({ meta: { inlineError: true }, mutationFn: (x: { id: string; body: Record<string, unknown> }) => api.send("PATCH", `/api/compliance/rules/${x.id}`, x.body) });
  if (isLoading) return <Loading />;
  if (error || !data) return <Empty>{errorMessage(error)}</Empty>;
  const unconfirmed = data.filter((r) => r.active && !r.confirmedAt).length;
  return (
    <Section
      title="Compliance rules"
      description="The due dates behind annual reports, franchise taxes and sales tax returns. They were prepared from the states' websites but could not all be checked page by page, so each starts unconfirmed: open its source, check the date and fee, change the date if needed, then confirm it."
    >
      {unconfirmed > 0 && <p className="mb-3 text-sm text-amber-700 dark:text-amber-300">{unconfirmed} active rule{unconfirmed === 1 ? " is" : "s are"} not yet confirmed.</p>}
      <ul className="divide-y divide-border rounded-md border border-border">
        {data.map((r) => (
          <li key={r.id} className={cn("flex flex-wrap items-start gap-x-4 gap-y-1 px-4 py-3 text-sm", !r.active && "opacity-60")}>
            <span className="min-w-0 flex-1">
              <span className="font-medium">
                {COMPLIANCE_STATES[r.jurisdiction] ?? r.jurisdiction} · {r.name}
              </span>
              <span className="block text-xs text-muted-foreground">
                {r.kind === "entity" ? `${r.entityTypes.map((t) => ENTITY_TYPES[t as keyof typeof ENTITY_TYPES] ?? t).join(", ")}${r.appliesTo === "any" ? "" : r.appliesTo === "domestic" ? " formed there" : " registered as foreign"}` : `Sales tax, filed ${FREQUENCY_WORDS[r.frequency as SalesTaxFrequency]}`}
              </span>
              <span className="block text-xs">{scheduleText(r)}</span>
              {r.fee && <span className="block text-xs text-muted-foreground">{r.fee}</span>}
              {r.sourceUrl && (
                <a href={r.sourceUrl} target="_blank" rel="noreferrer" className="block truncate text-xs text-primary hover:underline">
                  Source: {r.sourceUrl}
                </a>
              )}
            </span>
            <span className="flex flex-wrap items-center gap-2">
              {r.confirmedAt ? <span className={cn(pill, "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200")}>Confirmed {formatDate(r.confirmedAt.slice(0, 10))}</span> : <span className={cn(pill, "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200")}>Not confirmed</span>}
              {canEdit && (
                <>
                  <Button
                    size="sm"
                    variant={r.confirmedAt ? "ghost" : "outline"}
                    onClick={async () => {
                      if (r.confirmedAt || (await ask({ title: "Confirm this rule?", description: "Confirm only after checking the date and fee on the state's own page. Your name and today's date are recorded.", confirmLabel: "Confirm rule" }))) update.mutate({ id: r.id, body: { confirmed: !r.confirmedAt } });
                    }}
                  >
                    {r.confirmedAt ? "Unconfirm" : "Confirm"}
                  </Button>
                  {RULE_SETTINGS.some((d) => d.kind === "date-rule" && r.presetKey && d.presets?.includes(r.presetKey)) ? (
                    <Link href="/settings?tab=rules" className="inline-flex h-8 items-center px-2 text-sm text-primary hover:underline">
                      Change date in Rules and rates
                    </Link>
                  ) : r.schedule.kind !== "anniversary_month_end" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        const s = r.schedule;
                        const byDay = s.kind === "monthly_following" || s.kind === "quarterly_following";
                        const v = await askText({
                          title: "Change the due date",
                          description: byDay ? "The day of the following month the return is due (1 to 31)." : "The month and day it is due, as MM-DD (for example 04-15). Filings not yet made are dated again from today.",
                          label: byDay ? "Day of the month" : "Month and day",
                          defaultValue: byDay ? String(s.day) : `${String(s.month).padStart(2, "0")}-${String(s.day).padStart(2, "0")}`,
                          validate: (t) => (byDay ? (/^\d{1,2}$/.test(t.trim()) && Number(t) >= 1 && Number(t) <= 31 ? null : "Enter a day from 1 to 31.") : /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(t.trim()) ? null : "Use MM-DD, for example 04-15."),
                          confirmLabel: "Change date",
                        });
                        if (!v) return;
                        const t = v.trim();
                        const schedule = byDay ? { ...s, day: Number(t) } : { ...s, month: Number(t.slice(0, 2)), day: Number(t.slice(3, 5)) };
                        update.mutate({ id: r.id, body: { schedule } });
                      }}
                    >
                      Change date
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => update.mutate({ id: r.id, body: { active: !r.active } })}>
                    {r.active ? "Switch off" : "Switch on"}
                  </Button>
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
      {update.error && <p className="mt-2 text-sm text-destructive">{errorMessage(update.error)}</p>}
    </Section>
  );
}
